import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Converte URLs http(s) em texto pra <a target="_blank">, deixando o resto como string.
 * Apara pontuação trailing (`.`, `,`, `)`, `;`, `:`, `!`, `?`) que normalmente não é
 * parte da URL — assim "veja https://x.com." não vira link com o ponto incluso.
 *
 * Reaproveitado por chat e anotações pessoais. Click stopPropagation pra não
 * disparar handlers do container (ex.: abrir drawer ao clicar em link num card).
 */
export function renderTextoComLinks(texto: string, linkClasses: string): ReactNode[] {
  const regex = /(https?:\/\/[^\s]+)/gi;
  const partes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(texto)) !== null) {
    if (match.index > lastIndex) partes.push(texto.slice(lastIndex, match.index));
    let url = match[0];
    const trail = /[),.;:!?]+$/.exec(url);
    let suffix = "";
    if (trail) {
      suffix = trail[0];
      url = url.slice(0, -suffix.length);
    }
    partes.push(
      <a
        key={`lnk-${key++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );
    if (suffix) partes.push(suffix);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < texto.length) partes.push(texto.slice(lastIndex));
  return partes;
}

/** Detecta rapidamente se o texto contém alguma URL clicável. */
export function temLink(texto: string): boolean {
  return /https?:\/\/\S+/i.test(texto);
}

// ============================================================================
// Renderer rico: markdown leve (negrito, itálico, link [txt](url)) + auto URLs.
// Ordem de detecção importa — links markdown vêm antes de qualquer outro
// reconhecimento pra não quebrar `[texto **com negrito**](url)`.
// Internos (`/foo`) viram <Link to> do router (sem reload). Externos viram <a target=_blank>.
// ============================================================================

type Token =
  | { kind: "text"; value: string }
  | { kind: "img"; alt: string; url: string }
  | { kind: "link"; label: string; url: string }
  | { kind: "url"; url: string; trail: string }
  | { kind: "bold"; inner: Token[] }
  | { kind: "italic"; inner: Token[] }
  | { kind: "code"; value: string }
  | { kind: "highlight"; inner: Token[] }
  | { kind: "color"; cor: string; inner: Token[] }
  | { kind: "size"; nivel: "grande" | "pequeno"; inner: Token[] };

const COR_HEX: Record<string, string> = { vermelho: "#dc2626", verde: "#16a34a", azul: "#2563eb", laranja: "#ea580c", roxo: "#9333ea" };

/**
 * Resolve a cor do token pra um valor CSS. Aceita nome conhecido OU hex de 6
 * dígitos; **qualquer outra coisa vira `undefined`** (sem cor) em vez de ir
 * pro style — é o que impede um texto salvo malicioso de injetar CSS.
 */
function corSegura(cor: string): string | undefined {
  if (COR_HEX[cor]) return COR_HEX[cor];
  return /^#[0-9a-fA-F]{6}$/.test(cor) ? cor : undefined;
}

function tokenize(s: string): Token[] {
  const tokens: Token[] = [];
  let buf = "";
  let i = 0;
  const flush = () => { if (buf) { tokens.push({ kind: "text", value: buf }); buf = ""; } };

  while (i < s.length) {
    const rest = s.slice(i);

    // ![alt](url) imagem inline — precisa vir ANTES do link pra casar o "!".
    const mi = /^!\[([^\]\n]*)\]\(([^)\s]+)\)/.exec(rest);
    if (mi) { flush(); tokens.push({ kind: "img", alt: mi[1], url: mi[2] }); i += mi[0].length; continue; }

    // [label](url)
    const ml = /^\[([^\]\n]+)\]\(([^)\s]+)\)/.exec(rest);
    if (ml) { flush(); tokens.push({ kind: "link", label: ml[1], url: ml[2] }); i += ml[0].length; continue; }

    // http(s)://... auto-link
    const au = /^https?:\/\/[^\s)]+/.exec(rest);
    if (au) {
      flush();
      let url = au[0];
      const trail = (/[),.;:!?]+$/.exec(url) || [""])[0];
      url = url.slice(0, url.length - trail.length);
      tokens.push({ kind: "url", url, trail });
      i += au[0].length;
      continue;
    }

    // **negrito**
    const bo = /^\*\*([^*\n]+?)\*\*/.exec(rest);
    if (bo) { flush(); tokens.push({ kind: "bold", inner: tokenize(bo[1]) }); i += bo[0].length; continue; }

    // *italico* — exige conteúdo não-vazio sem * e sem espaço após o primeiro *
    const it = /^\*(?!\s)([^*\n]+?)\*(?!\*)/.exec(rest);
    if (it) { flush(); tokens.push({ kind: "italic", inner: tokenize(it[1]) }); i += it[0].length; continue; }

    // `código`
    const cd = /^`([^`\n]+?)`/.exec(rest);
    if (cd) { flush(); tokens.push({ kind: "code", value: cd[1] }); i += cd[0].length; continue; }

    // ==grifado==
    const hl = /^==([^=\n]+?)==/.exec(rest);
    if (hl) { flush(); tokens.push({ kind: "highlight", inner: tokenize(hl[1]) }); i += hl[0].length; continue; }

    // {cor}texto{/cor} — só cores nomeadas conhecidas (anti-XSS)
    const co = /^\{(vermelho|verde|azul|laranja|roxo)\}([\s\S]+?)\{\/\1\}/.exec(rest);
    if (co) { flush(); tokens.push({ kind: "color", cor: co[1], inner: tokenize(co[2]) }); i += co[0].length; continue; }

    // {#rrggbb}texto{/#rrggbb} — cor LIVRE (seletor arco-íris, jul/2026).
    // Continua seguro pelo mesmo motivo das nomeadas: o hex de 6 dígitos é
    // validado pela própria regex, então nada além de [0-9a-f] vira style.
    const cx = /^\{(#[0-9a-fA-F]{6})\}([\s\S]+?)\{\/\1\}/.exec(rest);
    if (cx) { flush(); tokens.push({ kind: "color", cor: cx[1], inner: tokenize(cx[2]) }); i += cx[0].length; continue; }

    // {grande}texto{/grande} e {pequeno}texto{/pequeno} — tamanho de fonte
    // (2ª leva de jul/2026). Nomes fixos, então nada de style arbitrário.
    const sz = /^\{(grande|pequeno)\}([\s\S]+?)\{\/\1\}/.exec(rest);
    if (sz) { flush(); tokens.push({ kind: "size", nivel: sz[1] as "grande" | "pequeno", inner: tokenize(sz[2]) }); i += sz[0].length; continue; }

    buf += s[i];
    i++;
  }
  flush();
  return tokens;
}

function renderTokens(tokens: Token[], linkClasses: string, keyRef: { v: number }): ReactNode[] {
  return tokens.map((tk) => {
    const k = keyRef.v++;
    switch (tk.kind) {
      case "text": return tk.value;
      case "img":   return renderRichImage(tk.url, tk.alt, k);
      case "link":  return renderRichLink(tk.url, tk.label, linkClasses, k);
      case "url":   return [renderRichLink(tk.url, tk.url, linkClasses, k), tk.trail];
      case "bold":  return <strong key={`b${k}`}>{renderTokens(tk.inner, linkClasses, keyRef)}</strong>;
      case "italic":return <em key={`i${k}`}>{renderTokens(tk.inner, linkClasses, keyRef)}</em>;
      case "code":  return <code key={`c${k}`} className="font-mono text-[0.9em] bg-black/5 dark:bg-white/10 px-1 py-0.5 rounded">{tk.value}</code>;
      case "highlight": return <mark key={`h${k}`} className="bg-yellow-200 dark:bg-yellow-500/40 rounded px-0.5">{renderTokens(tk.inner, linkClasses, keyRef)}</mark>;
      case "color": return <span key={`co${k}`} style={{ color: corSegura(tk.cor) }}>{renderTokens(tk.inner, linkClasses, keyRef)}</span>;
      case "size":  return <span key={`sz${k}`} style={{ fontSize: tk.nivel === "grande" ? "1.3em" : "0.82em" }}>{renderTokens(tk.inner, linkClasses, keyRef)}</span>;
    }
  });
}

/** Imagem inline `![alt](url)`. Só renderiza <img> pra http(s) ou caminho interno
 * (/uploads...). Qualquer outro esquema vira texto (anti-XSS, igual aos links). */
/** Caminho interno seguro: "/algo" mas não "//host" nem "/\host" (o browser
 * normaliza "\" → "/", virando protocol-relative pra domínio externo). */
function caminhoInterno(url: string): boolean {
  return url.startsWith("/") && url[1] !== "/" && url[1] !== "\\";
}

function renderRichImage(url: string, alt: string, key: number): ReactNode {
  const seguro = /^https?:\/\//i.test(url) || caminhoInterno(url);
  if (!seguro) return <span key={`si${key}`}>{alt || url}</span>;
  return (
    <img
      key={`im${key}`}
      src={url}
      alt={alt || ""}
      className="max-w-full max-h-80 rounded-lg my-1 border border-slate-200 dark:border-slate-700"
      loading="lazy"
    />
  );
}

function renderRichLink(url: string, label: string, linkClasses: string, key: number): ReactNode {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  // Path interno do app vira <Link> — sem reload, mantém estado da SPA.
  // Exige "/" mas NÃO "//host" nem "/\host" (protocolo-relativo abre domínio externo).
  if (caminhoInterno(url)) {
    return (
      <Link key={`L${key}`} to={url} className={linkClasses} onClick={stop}>
        {label}
      </Link>
    );
  }
  // Externo: SOMENTE http/https. Bloqueia javascript:, data:, //host etc.
  // (anti-XSS armazenado e open-redirect — texto vem de usuários/clientes).
  if (/^https?:\/\//i.test(url)) {
    return (
      <a
        key={`A${key}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
        onClick={stop}
      >
        {label}
      </a>
    );
  }
  // Protocolo não permitido: renderiza só o texto (sem href).
  return <span key={`S${key}`}>{label}</span>;
}

// ---------------------------------------------------------------------------
// Blocos de LINHA (2ª leva jul/2026): listas, numeração e checklist.
// `- item` → lista • `1. item` → numerada • `- [ ]`/`- [x]` → checklist.
// Como agrupam por linha, o render passou a ser LINE-BASED: agrupa linhas do
// mesmo tipo num bloco e o resto continua inline (com <br> entre as linhas).
// ---------------------------------------------------------------------------
const RE_CHECK = /^- \[([ xX])\]\s?(.*)$/;      // checklist (antes do bullet!)
const RE_BULLET = /^[-*]\s+(.*)$/;               // lista com marcador
const RE_NUM = /^\d+\.\s+(.*)$/;                 // lista numerada

function ehLista(l: string) { return RE_CHECK.test(l) || RE_BULLET.test(l) || RE_NUM.test(l); }
function renderInline(l: string, cls: string, kr: { v: number }): ReactNode[] {
  return renderTokens(tokenize(l), cls, kr);
}

/**
 * Renderer rico — markdown leve: **negrito**, *itálico*, ==grifo==, `código`,
 * {cor}, {grande}/{pequeno}, [label](url), imagem, URLs, e BLOCOS: listas,
 * numeração e checklist. Quebras de linha viram <br> (não depende mais só do
 * whitespace-pre-wrap do container). Usado em comentários, ajustes, notas, chat.
 */
export function renderTextoRico(
  texto: string,
  linkClasses: string,
  onToggleCheck?: (idx: number) => void,
): ReactNode[] {
  const linhas = (texto || "").split("\n");
  const out: ReactNode[] = [];
  const kr = { v: 0 };
  let i = 0;
  let bk = 0;
  let checkIdx = 0; // índice do item de checklist no texto inteiro (pro toggle)

  while (i < linhas.length) {
    const linha = linhas[i];

    // CHECKLIST — testar ANTES do bullet, porque "- [ ]" também casa "- ".
    if (RE_CHECK.test(linha)) {
      const itens: ReactNode[] = [];
      while (i < linhas.length) {
        const m = RE_CHECK.exec(linhas[i]);
        if (!m) break;
        const feito = m[1].toLowerCase() === "x";
        const idx = checkIdx++;
        const caixaCls =
          "mt-[3px] w-3.5 h-3.5 rounded border shrink-0 inline-flex items-center justify-center text-[9px] leading-none " +
          (feito ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-400 dark:border-slate-500");
        itens.push(
          <li key={`ck${bk}-${i}`} className="flex items-start gap-1.5">
            {onToggleCheck ? (
              // Clicável: marca/desmarca o item já registrado (pedido do Ricardo).
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleCheck(idx); }}
                className={caixaCls + " cursor-pointer hover:border-emerald-500"}
                title={feito ? "Desmarcar" : "Marcar como feito"}
                aria-label={feito ? "Desmarcar item" : "Marcar item"}
              >
                {feito ? "✓" : ""}
              </button>
            ) : (
              <span className={caixaCls}>{feito ? "✓" : ""}</span>
            )}
            <span className={feito ? "line-through opacity-70" : ""}>{renderInline(m[2], linkClasses, kr)}</span>
          </li>,
        );
        i++;
      }
      out.push(<ul key={`ulc${bk++}`} className="my-1 space-y-0.5">{itens}</ul>);
      continue;
    }

    // NUMERADA
    if (RE_NUM.test(linha)) {
      const itens: ReactNode[] = [];
      while (i < linhas.length) {
        const m = RE_NUM.exec(linhas[i]);
        if (!m) break;
        itens.push(<li key={`ol${bk}-${i}`}>{renderInline(m[1], linkClasses, kr)}</li>);
        i++;
      }
      out.push(<ol key={`oln${bk++}`} className="my-1 pl-5 list-decimal space-y-0.5">{itens}</ol>);
      continue;
    }

    // BULLET
    if (RE_BULLET.test(linha)) {
      const itens: ReactNode[] = [];
      while (i < linhas.length && RE_BULLET.test(linhas[i]) && !RE_CHECK.test(linhas[i])) {
        const m = RE_BULLET.exec(linhas[i])!;
        itens.push(<li key={`ul${bk}-${i}`}>{renderInline(m[1], linkClasses, kr)}</li>);
        i++;
      }
      out.push(<ul key={`uln${bk++}`} className="my-1 pl-5 list-disc space-y-0.5">{itens}</ul>);
      continue;
    }

    // LINHAS NORMAIS — agrupa consecutivas, <br> entre elas.
    const normais: string[] = [];
    while (i < linhas.length && !ehLista(linhas[i])) { normais.push(linhas[i]); i++; }
    out.push(
      <span key={`p${bk++}`}>
        {normais.map((ln, idx) => (
          <span key={idx}>
            {renderInline(ln, linkClasses, kr)}
            {idx < normais.length - 1 && <br />}
          </span>
        ))}
      </span>,
    );
  }

  return out;
}
